import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Wrench,
  Plus,
  Edit,
  Trash2,
  Search,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Package,
  TrendingUp,
  LayoutDashboard,
  ShoppingBag,
  Settings,
  Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_CATEGORIES = [
  { id: "scaffolding", name: "Scaffolding", color: "bg-blue-500" },
  { id: "safety", name: "Safety Equipment", color: "bg-green-500" },
  { id: "power_tools", name: "Power Tools", color: "bg-orange-500" },
  { id: "hand_tools", name: "Hand Tools", color: "bg-purple-500" },
  { id: "lifting", name: "Lifting Equipment", color: "bg-red-500" },
];

export default function AdminRentals() {
  const { t } = useTranslation();
  const [tools, setTools] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [toolForm, setToolForm] = useState({
    name: "",
    description: "",
    category: "scaffolding",
    daily_rate: "",
    weekly_rate: "",
    condition: "good",
    photos: [],
    available: true
  });

  useEffect(() => {
    fetchTools();
    fetchRentals();
  }, []);

  const fetchTools = async () => {
    try {
      const res = await axios.get(`${API}/tools`);
      setTools(res.data);
    } catch (err) {
      console.error("Error fetching tools:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRentals = async () => {
    try {
      const res = await axios.get(`${API}/rentals`);
      setRentals(res.data);
    } catch (err) {
      console.error("Error fetching rentals:", err);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];
    
    for (const file of files) {
      try {
        // Get Cloudinary signature from backend
        const sigRes = await axios.get(`${API}/cloudinary/signature?folder=rentals&resource_type=image`);
        const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
        
        // Upload directly to Cloudinary
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", api_key);
        formData.append("timestamp", timestamp);
        formData.append("signature", signature);
        formData.append("folder", folder);
        
        const cloudinaryRes = await fetch(
          `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
          { method: "POST", body: formData }
        );
        
        const cloudinaryData = await cloudinaryRes.json();
        
        if (cloudinaryData.secure_url) {
          uploadedUrls.push(cloudinaryData.secure_url);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (err) {
        console.error("Upload error:", err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setToolForm(prev => ({
      ...prev,
      photos: [...prev.photos, ...uploadedUrls]
    }));
    setUploading(false);
  };

  const removePhoto = (index) => {
    setToolForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const saveTool = async () => {
    if (!toolForm.name || !toolForm.daily_rate) {
      toast.error("Name and daily rate are required");
      return;
    }

    try {
      const data = {
        ...toolForm,
        daily_rate: parseFloat(toolForm.daily_rate),
        weekly_rate: toolForm.weekly_rate ? parseFloat(toolForm.weekly_rate) : null
      };

      if (editingTool) {
        await axios.put(`${API}/tools/${editingTool.tool_id}`, data);
        toast.success("Tool updated!");
      } else {
        await axios.post(`${API}/tools`, data);
        toast.success("Tool added!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTools();
    } catch (err) {
      toast.error("Failed to save tool");
    }
  };

  const deleteTool = async (toolId) => {
    if (!confirm("Are you sure you want to delete this tool?")) return;
    
    try {
      await axios.delete(`${API}/tools/${toolId}`);
      toast.success("Tool deleted");
      fetchTools();
    } catch (err) {
      toast.error("Failed to delete tool");
    }
  };

  const openEditDialog = (tool) => {
    setEditingTool(tool);
    setToolForm({
      name: tool.name,
      description: tool.description || "",
      category: tool.category,
      daily_rate: tool.daily_rate.toString(),
      weekly_rate: tool.weekly_rate?.toString() || "",
      condition: tool.condition || "good",
      photos: tool.photos || [],
      available: tool.available
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTool(null);
    setToolForm({
      name: "",
      description: "",
      category: "scaffolding",
      daily_rate: "",
      weekly_rate: "",
      condition: "good",
      photos: [],
      available: true
    });
  };

  const updateRentalStatus = async (rentalId, status) => {
    try {
      await axios.put(`${API}/rentals/${rentalId}/status`, { status });
      toast.success(`Rental ${status}`);
      fetchRentals();
      fetchTools();
    } catch (err) {
      toast.error("Failed to update rental");
    }
  };

  const filteredTools = tools.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Stats
  const totalTools = tools.length;
  const availableTools = tools.filter(t => t.available).length;
  const pendingRentals = rentals.filter(r => r.status === "pending").length;
  const activeRentals = rentals.filter(r => r.status === "active").length;

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Rentals Admin">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title="Tool Rentals">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border-cyan-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Wrench className="w-8 h-8 text-cyan-400" />
                <div>
                  <p className="text-[#94A3B8] text-sm">Total Tools</p>
                  <p className="text-2xl font-bold text-white">{totalTools}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-[#94A3B8] text-sm">Available</p>
                  <p className="text-2xl font-bold text-white">{availableTools}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-[#94A3B8] text-sm">Pending</p>
                  <p className="text-2xl font-bold text-white">{pendingRentals}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-[#94A3B8] text-sm">Active Rentals</p>
                  <p className="text-2xl font-bold text-white">{activeRentals}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Rentals */}
        {pendingRentals > 0 && (
          <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
            <CardHeader>
              <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                Pending Rental Requests ({pendingRentals})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {rentals.filter(r => r.status === "pending").map((rental) => (
                  <div
                    key={rental.rental_id}
                    className="flex items-center justify-between p-4 bg-white/5 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                        <Wrench className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{rental.tool_name || "Tool"}</p>
                        <p className="text-sm text-[#94A3B8]">
                          Requested by {rental.renter_name} • {rental.start_date} to {rental.end_date}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => updateRentalStatus(rental.rental_id, "approved")}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateRentalStatus(rental.rental_id, "cancelled")}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tools Section */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <Wrench className="w-5 h-5 text-[#23F7DD]" />
              Tool Inventory
            </CardTitle>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search tools..."
                  className="pl-9 bg-black/50 border-white/10 text-white"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-white"
              >
                <option value="">All Categories</option>
                {TOOL_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Tool
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-['Unbounded']">
                      {editingTool ? "Edit Tool" : "Add New Tool"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">Tool Name *</label>
                      <Input
                        value={toolForm.name}
                        onChange={(e) => setToolForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Adjustable Scaffold Jack"
                        className="bg-black/50 border-white/10 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">Description</label>
                      <Textarea
                        value={toolForm.description}
                        onChange={(e) => setToolForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Tool description..."
                        className="bg-black/50 border-white/10 text-white"
                        rows={3}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Category</label>
                        <select
                          value={toolForm.category}
                          onChange={(e) => setToolForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full bg-black/50 border border-white/10 rounded-lg p-2 text-white"
                        >
                          {TOOL_CATEGORIES.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Condition</label>
                        <select
                          value={toolForm.condition}
                          onChange={(e) => setToolForm(prev => ({ ...prev, condition: e.target.value }))}
                          className="w-full bg-black/50 border border-white/10 rounded-lg p-2 text-white"
                        >
                          <option value="new">New</option>
                          <option value="good">Good</option>
                          <option value="fair">Fair</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Daily Rate (NOK) *</label>
                        <Input
                          type="number"
                          value={toolForm.daily_rate}
                          onChange={(e) => setToolForm(prev => ({ ...prev, daily_rate: e.target.value }))}
                          placeholder="150"
                          className="bg-black/50 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Weekly Rate (NOK)</label>
                        <Input
                          type="number"
                          value={toolForm.weekly_rate}
                          onChange={(e) => setToolForm(prev => ({ ...prev, weekly_rate: e.target.value }))}
                          placeholder="800"
                          className="bg-black/50 border-white/10 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={toolForm.available}
                        onChange={(e) => setToolForm(prev => ({ ...prev, available: e.target.checked }))}
                        className="w-4 h-4 rounded border-white/20"
                      />
                      <label className="text-sm text-white">Available for rent</label>
                    </div>
                    
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">Tool Images</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {toolForm.photos.map((url, index) => (
                          <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden">
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            <button
                              onClick={() => removePhoto(index)}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                            >
                              <Trash2 className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#23F7DD]/50">
                        <Package className="w-5 h-5 text-[#94A3B8]" />
                        <span className="text-[#94A3B8]">{uploading ? "Uploading..." : "Add Images"}</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                    </div>
                    
                    <Button
                      onClick={saveTool}
                      className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                    >
                      {editingTool ? "Update Tool" : "Add Tool"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {filteredTools.length === 0 ? (
              <div className="text-center py-12 text-[#94A3B8]">
                <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tools found</p>
                <p className="text-sm mt-1">Add your first tool to start renting</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTools.map((tool) => (
                  <div
                    key={tool.tool_id}
                    className="bg-white/5 rounded-lg border border-white/10 overflow-hidden hover:border-[#23F7DD]/50 transition-colors"
                  >
                    <div className="aspect-video bg-black/50 relative">
                      {tool.photos && tool.photos[0] ? (
                        <img
                          src={tool.photos[0]}
                          alt={tool.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Wrench className="w-12 h-12 text-[#475569]" />
                        </div>
                      )}
                      <Badge 
                        className={`absolute top-2 right-2 ${
                          tool.available ? "bg-green-500" : "bg-red-500"
                        }`}
                      >
                        {tool.available ? "Available" : "Rented"}
                      </Badge>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-white font-medium">{tool.name}</h4>
                          <p className="text-xs text-[#94A3B8] capitalize">{tool.category.replace("_", " ")}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div>
                          <p className="text-[#23F7DD] font-bold">{tool.daily_rate} NOK<span className="text-xs text-[#94A3B8]">/day</span></p>
                          {tool.weekly_rate && (
                            <p className="text-xs text-[#94A3B8]">{tool.weekly_rate} NOK/week</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(tool)}
                            className="text-[#94A3B8] hover:text-[#23F7DD]"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteTool(tool.tool_id)}
                            className="text-[#94A3B8] hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
